import React, { useState, useRef } from 'react';
import {
  Card,
  Row,
  Col,
  Statistic,
  Table,
  Typography,
  Space,
  Button,
  Select,
  DatePicker,
  Tag,
  Progress,
  Badge,
  Dropdown,
  Menu,
  List,
  Avatar
} from 'antd';
import {
  MedicineBoxOutlined,
  ShoppingCartOutlined,
  DollarOutlined,
  ArrowUpOutlined,
  ArrowDownOutlined,
  ReloadOutlined,
  ExportOutlined,
  LineChartOutlined,
  BarChartOutlined,
  FilterOutlined,
  RiseOutlined,
  FallOutlined,
  GiftOutlined
} from '@ant-design/icons';
import { useQuery } from 'react-query';
import { dashboardService, DashboardData, IncomeHistoryTransaction } from '../../services/dashboardService';
import dayjs from 'dayjs';
import {
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Area,
  Bar,
  ComposedChart,
  Legend,
  PieChart,
  Pie,
  Cell
} from 'recharts';
import styles from './DashboardPage.module.css';

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;
const { Option } = Select;

const DashboardPage: React.FC = () => {
  const [selectedMonth, setSelectedMonth] = useState(dayjs());
  const [selectedCategory, setSelectedCategory] = useState<string>('all');

  // Ref cho các bảng chi tiết
  const lowStockRef = useRef<HTMLDivElement>(null);
  const expiringRef = useRef<HTMLDivElement>(null);

  // Hàm xử lý click vào card thống kê
  const handleStatisticClick = (key: string) => {
    if (key === 'low_stock') {
      lowStockRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    } else if (key === 'expiring') {
      expiringRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    } else {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  // Fetch dashboard data
  const { data: dashboardData, error: dashboardError } = useQuery(
    ['dashboard', selectedMonth],
    () => dashboardService.getDashboardData({ 
      month: selectedMonth.format('YYYY-MM')
    }),
    {
      staleTime: 5 * 60 * 1000,
      retry: 3,
      onError: (error) => {
        console.error('Dashboard API error:', error);
      }
    }
  );

  // Fetch income history
  const { data: incomeHistory, error: incomeHistoryError } = useQuery(
    ['income-history', selectedMonth],
    () => dashboardService.getIncomeHistory({ 
      month: selectedMonth.format('YYYY-MM'),
      limit: 10
    }),
    {
      staleTime: 5 * 60 * 1000,
      retry: 3,
      onError: (error) => {
        console.error('Income history API error:', error);
      }
    }
  );

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('vi-VN', {
      style: 'currency',
      currency: 'VND'
    }).format(amount);
  };

  const getBalance = () => {
    if (!dashboardData) return 0;
    return dashboardData.totalIncome + dashboardData.totalGifts - dashboardData.totalExpenses;
  };

  const getMonthlyBalance = () => {
    if (!dashboardData) return 0;
    return dashboardData.monthlyIncome + dashboardData.monthlyGifts - dashboardData.monthlyExpenses;
  };

  // Loading state
  if (dashboardError) {
    return (
      <div style={{ padding: '24px', textAlign: 'center' }}>
        <Text type="danger">Lỗi khi tải dữ liệu dashboard</Text>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      {/* Header */}
      <div className={styles.header}>
        <Title level={2} className={styles.title}>
          Dashboard
        </Title>
        <div className={styles.headerControls}>
          <DatePicker
            picker="month"
            value={selectedMonth}
            onChange={(date) => {
              if (date) {
                setSelectedMonth(date);
              }
            }}
            format="MM/YYYY"
            placeholder="Chọn tháng"
            className={styles.monthPicker}
          />
        </div>
      </div>

      {/* Main Content */}
      <div className={styles.content}>
        {/* Balance Overview */}
        <Row gutter={[16, 16]} className={styles.balanceSection}>
          <Col xs={24} sm={12} lg={6}>
            <Card className={styles.balanceCard}>
              <Statistic
                title="Số dư hiện tại"
                value={getBalance()}
                precision={0}
                valueStyle={{ color: '#3f8600' }}
                prefix={<DollarOutlined />}
                formatter={(value) => formatCurrency(value as number)}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} lg={6}>
            <Card className={styles.balanceCard}>
              <Statistic
                title="Thu nhập"
                value={dashboardData?.totalIncome || 0}
                precision={0}
                valueStyle={{ color: '#52c41a' }}
                prefix={<ArrowUpOutlined />}
                formatter={(value) => formatCurrency(value as number)}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} lg={6}>
            <Card className={styles.balanceCard}>
              <Statistic
                title="Quà tặng"
                value={dashboardData?.totalGifts || 0}
                precision={0}
                valueStyle={{ color: '#1890ff' }}
                prefix={<GiftOutlined />}
                formatter={(value) => formatCurrency(value as number)}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} lg={6}>
            <Card className={styles.balanceCard}>
              <Statistic
                title="Chi tiêu"
                value={dashboardData?.totalExpenses || 0}
                precision={0}
                valueStyle={{ color: '#cf1322' }}
                prefix={<ArrowDownOutlined />}
                formatter={(value) => formatCurrency(value as number)}
              />
            </Card>
          </Col>
        </Row>

        {/* Monthly Overview */}
        <Row gutter={[16, 16]} className={styles.monthlySection}>
          <Col xs={24} lg={12}>
            <Card title="Thống kê tháng" className={styles.monthlyCard}>
              <Row gutter={[16, 16]}>
                <Col span={12}>
                  <Statistic
                    title="Thu nhập tháng"
                    value={dashboardData?.monthlyIncome || 0}
                    precision={0}
                    valueStyle={{ color: '#52c41a' }}
                    prefix={<RiseOutlined />}
                    formatter={(value) => formatCurrency(value as number)}
                  />
                </Col>
                <Col span={12}>
                  <Statistic
                    title="Chi tiêu tháng"
                    value={dashboardData?.monthlyExpenses || 0}
                    precision={0}
                    valueStyle={{ color: '#cf1322' }}
                    prefix={<FallOutlined />}
                    formatter={(value) => formatCurrency(value as number)}
                  />
                </Col>
              </Row>
              <div style={{ marginTop: '16px' }}>
                <Text strong>Số dư tháng: </Text>
                <Text 
                  strong 
                  style={{ 
                    color: getMonthlyBalance() >= 0 ? '#52c41a' : '#cf1322',
                    fontSize: '18px'
                  }}
                >
                  {formatCurrency(getMonthlyBalance())}
                </Text>
              </div>
            </Card>
          </Col>
          <Col xs={24} lg={12}>
            <Card title="Top danh mục chi tiêu" className={styles.categoriesCard}>
              {dashboardData?.topExpenseCategories.map((category, index) => (
                <div key={index} style={{ marginBottom: '12px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                    <Text>{category.name}</Text>
                    <Text strong>{formatCurrency(category.amount)}</Text>
                  </div>
                  <Progress 
                    percent={category.percentage} 
                    strokeColor="#ff89a1"
                    showInfo={false}
                    size="small"
                  />
                  <Text type="secondary" style={{ fontSize: '12px' }}>
                    {category.percentage}%
                  </Text>
                </div>
              ))}
            </Card>
          </Col>
        </Row>

        {/* Income History */}
        {incomeHistory?.transactions && incomeHistory.transactions.length > 0 && (
          <Row gutter={[16, 16]} className={styles.incomeHistorySection}>
            <Col span={24}>
              <Card 
                title="Lịch sử thu nhập" 
                className={styles.incomeHistoryCard}
                extra={
                  <Button type="link" onClick={() => window.location.href = '/income'}>
                    Xem tất cả
                  </Button>
                }
              >
                <List
                  dataSource={incomeHistory.transactions}
                  renderItem={(transaction) => (
                    <List.Item>
                      <List.Item.Meta
                        avatar={
                          <Avatar 
                            icon={<ArrowUpOutlined />}
                            style={{ backgroundColor: '#52c41a' }}
                          />
                        }
                        title={
                          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                            <Text strong>{transaction.source_name}</Text>
                            <Text strong style={{ color: '#52c41a' }}>
                              +{formatCurrency(transaction.amount)}
                            </Text>
                          </div>
                        }
                        description={
                          <div>
                            <Text type="secondary">{transaction.notes || 'Thu nhập'}</Text>
                            <br />
                            <Text type="secondary" style={{ fontSize: '12px' }}>
                              {transaction.wallet_name} • {dayjs(transaction.income_date).format('DD/MM/YYYY')}
                            </Text>
                          </div>
                        }
                      />
                    </List.Item>
                  )}
                />
              </Card>
            </Col>
          </Row>
        )}

        {/* Recent Transactions */}
        {dashboardData?.recentTransactions && dashboardData.recentTransactions.length > 0 && (
          <Row gutter={[16, 16]} className={styles.transactionsSection}>
            <Col span={24}>
              <Card 
                title="Giao dịch gần đây" 
                className={styles.transactionsCard}
                extra={
                  <Button type="link" onClick={() => window.location.href = '/expenses'}>
                    Xem tất cả
                  </Button>
                }
              >
                <List
                  dataSource={dashboardData.recentTransactions}
                  renderItem={(transaction) => (
                    <List.Item>
                      <List.Item.Meta
                        avatar={
                          <Avatar 
                            icon={
                              transaction.type === 'expense' ? <ArrowDownOutlined /> :
                              transaction.type === 'income' ? <ArrowUpOutlined /> :
                              <GiftOutlined />
                            }
                            style={{ 
                              backgroundColor: 
                                transaction.type === 'expense' ? '#cf1322' :
                                transaction.type === 'income' ? '#52c41a' : '#1890ff'
                            }}
                          />
                        }
                        title={
                          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                            <Text strong>{transaction.description}</Text>
                            <Text 
                              strong 
                              style={{ 
                                color: transaction.type === 'expense' ? '#cf1322' : '#52c41a'
                              }}
                            >
                              {transaction.type === 'expense' ? '- ' : '+ '}
                              {formatCurrency(transaction.amount)}
                            </Text>
                          </div>
                        }
                        description={
                          <Text type="secondary">
                            {dayjs(transaction.date).format('DD/MM/YYYY')}
                          </Text>
                        }
                      />
                    </List.Item>
                  )}
                />
              </Card>
            </Col>
          </Row>
        )}
      </div>
    </div>
  );
};

export default DashboardPage; 